import Select from 'ol/interaction/Select';
import VectorSource from 'ol/source/Vector';
import { clear } from 'ol/obj';
import { includes } from 'ol/array';
import Event from 'ol/events/Event';

import { OverlayVectorLayer } from 'cadenza/map/interactions/overlay-vector-layer';

// Custom select interaction maintains custom vector layer overlay for the selection handles.
// It is rendered on top of the layer, so the handles are always on top.
// The overlay ignores the style of each individual feature and uses always layer style function, which produces a half-transparent only-selection style.
export class SelectOverlayInteraction extends Select {

  #overlay;
  _additionalFeaturesFn;

  constructor (options, additionalFeaturesFn) {
    super({
      ...options,
      style: null
    });
    this._additionalFeaturesFn = additionalFeaturesFn;
    this.#overlay = new OverlayVectorLayer({
      source: new VectorSource({
        features: this.getFeatures()
      }),
      style: options.getSelectStyle,
      getMap: () => this.getMap()
    });
  }

  setMap (map) {
    super.setMap(map);
    this.#overlay.setMap(map);
  }

  handleEvent (mapBrowserEvent) {
    if (!this._additionalFeaturesFn) {
      return super.handleEvent(mapBrowserEvent);
    }
    // copied and modified from open-layers/Select.js
    /*
BSD 2-Clause License

Copyright 2005-present, OpenLayers Contributors All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:

    Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.

    Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
     */
    if (!this.condition_(mapBrowserEvent)) {
      return true;
    }
    const add = this.addCondition_(mapBrowserEvent);
    const remove = this.removeCondition_(mapBrowserEvent);
    const toggle = this.toggleCondition_(mapBrowserEvent);
    const set = !add && !remove && !toggle;
    const map = mapBrowserEvent.map;
    const features = this.getFeatures();
    const deselected = [];
    const selected = [];
    if (set) {
      // Replace the currently selected feature(s) with the feature(s) at the
      // pixel, or clear the selected feature(s) if there is no feature at
      // the pixel.
      clear(this.featureLayerAssociation_);
      const hitFeatures = [];
      map.forEachFeatureAtPixel(
        mapBrowserEvent.pixel,
        (feature, layer) => hitFeatures.push([ feature, layer ]),
        {
          layerFilter: this.layerFilter_,
          hitTolerance: this.hitTolerance_
        });
      this._additionalFeaturesFn(hitFeatures).forEach(([ feature, layer ]) => {
        if (this.filter_(feature, layer)) {
          selected.push(feature);
          this.addFeatureLayerAssociation_(feature, layer);
          return !this.multi_;
        }
      });

      for (let i = features.getLength() - 1; i >= 0; --i) {
        const feature = features.item(i);
        const index = selected.indexOf(feature);
        if (index > -1) {
          // feature is already selected
          selected.splice(index, 1);
        } else {
          features.remove(feature);
          deselected.push(feature);
        }
      }
      if (selected.length !== 0) {
        features.extend(selected);
      }
    } else {
      // Modify the currently selected feature(s).
      const hitFeatures = [];
      map.forEachFeatureAtPixel(
        mapBrowserEvent.pixel,
        (feature, layer) => hitFeatures.push([ feature, layer ]),
        {
          layerFilter: this.layerFilter_,
          hitTolerance: this.hitTolerance_
        });
      this._additionalFeaturesFn(hitFeatures).forEach(([ feature, layer ]) => {
        if (this.filter_(feature, layer)) {
          if ((add || toggle) && !includes(features.getArray(), feature)) {
            selected.push(feature);
            this.addFeatureLayerAssociation_(feature, layer);
          } else if ((remove || toggle)
            && includes(features.getArray(), feature)) {
            deselected.push(feature);
            this.removeFeatureLayerAssociation_(feature);
          }
          return !this.multi_;
        }
      });
      for (let j = deselected.length - 1; j >= 0; --j) {
        features.remove(deselected[j]);
      }
      features.extend(selected);
    }
    if (selected.length > 0 || deselected.length > 0) {
      this.dispatchEvent(new SelectEvent('select', selected, deselected, mapBrowserEvent));
    }
    return true;
    // end of copied code
  }

}

class SelectEvent extends Event {

  constructor (type, selected, deselected, mapBrowserEvent) {
    super(type);
    this.selected = selected;
    this.deselected = deselected;
    this.mapBrowserEvent = mapBrowserEvent;
  }

}
